import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  ArrowLeftIcon,
  Flex,
  Typography,
  getColorCSSVar,
} from '@iheartjane/reefer';
import { useQueryClient } from '@tanstack/react-query';
import capitalize from 'lodash-es/capitalize';
import { useCallback, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { useComplianceLanguage } from '@jane/kiosk/providers';
import { useGetStore } from '@jane/shared/data-access';
import type { Store, StoreSpecial } from '@jane/shared/types';

import {
  HeaderIcons,
  HeaderWithIcons,
  IconButton,
  SignInButton,
} from '../../components';
import { useGetCart, useKioskUser } from '../../data-access';
import type { RootLoaderType, SpecialLoaderType } from '../../data-access';
import {
  useKioskSpacing,
  useTypedLoaderData,
  useTypedRouteLoaderData,
} from '../../hooks';
import { SpecialInner } from './SpecialInner';
import { SpecialInnerOld } from './SpecialInnerOld';

export const Special = () => {
  const specialsRedesignMenu = useFlag(FLAGS.c3SpecialsRedesignMenu);
  const { isIpad, getKioskSpacing } = useKioskSpacing();
  const { getWord } = useComplianceLanguage();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const previousCategory = useRef(state?.menuCategory ?? '');

  const { special } = useTypedLoaderData<SpecialLoaderType>();
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { data: cart } = useGetCart();
  const { data: kioskUser } = useKioskUser();
  const signedIn = !!kioskUser;
  const showSignIn =
    store?.kiosk_anonymous_checkout !== 'required' && store?.kiosk_allow_auth;

  const navigateToMenu = useCallback(() => {
    navigate(`/stores/${storeId}/menu/${previousCategory.current}`, {
      state: { resetSearch: true },
    });
  }, [navigate, storeId]);

  const onSearchIconClick = useCallback(() => {
    navigate(`${pathname}/search`);
  }, [navigate, pathname]);

  const onLoginIconClick = useCallback(() => {
    navigate(`${pathname}/login`);
  }, [navigate, pathname]);

  const onLogoutIconClick = useCallback(() => {
    navigate(`${pathname}/logout`);
  }, [navigate, pathname]);

  const onCartButtonClick = useCallback(() => {
    queryClient.invalidateQueries(['cart']);
    navigate(`/stores/${storeId}/cart`);
  }, [navigate, storeId]);

  return (
    <>
      <HeaderWithIcons
        backgroundColor={getColorCSSVar('info')}
        signedIn={signedIn}
        sticky
        id="store-special-page-header"
      >
        <Flex flexDirection="column" width="100%">
          {showSignIn && !signedIn && (
            <SignInButton
              location="special"
              ml="auto"
              mb={getKioskSpacing(32, 16)}
              onLoginIconClick={onLoginIconClick}
            />
          )}
          <Flex alignItems="center" mt={getKioskSpacing(12, 8)}>
            <IconButton
              ariaLabel="back button"
              onClick={navigateToMenu}
              icon={<ArrowLeftIcon size={isIpad ? 'lg' : 'xl'} />}
            />
            <Typography
              as="h1"
              variant="huge"
              color="grays-white"
              ml={isIpad ? 16 : 32}
              maxLines={2}
              style={{ paddingBottom: '1px', paddingTop: '1px' }}
            >
              {specialsRedesignMenu
                ? capitalize(getWord('deals') ?? 'Deal')
                : special.title}
            </Typography>
            <Flex grow={1} justifyContent="flex-end">
              <HeaderIcons
                cart={cart}
                onCartButtonClick={onCartButtonClick}
                onLoginIconClick={onLoginIconClick}
                onLogoutIconClick={onLogoutIconClick}
                onSearchIconClick={onSearchIconClick}
                signedIn={signedIn}
                storeId={store.id}
              />
            </Flex>
          </Flex>
        </Flex>
      </HeaderWithIcons>
      {specialsRedesignMenu ? (
        <SpecialInner
          navigateToCart={onCartButtonClick}
          navigateToMenu={navigateToMenu}
          storeSpecial={special}
          store={store}
        />
      ) : (
        <SpecialInnerOld
          cart={cart}
          special={special as StoreSpecial}
          store={store}
        />
      )}
      <Outlet />
    </>
  );
};
